import {
    collection,
    countUpdates,
    parentTypeFor,
    readCount,
    refs,
    superRefs,
} from "../_variants/index.js";
import {
    createBasicYellowCard,
    jobCardSignoffCheckList
} from "../JobCards/actions.js";

import "firebase/functions";

import { appConfig } from "../../../utils.js";
import { createBasicDocuments } from "./postActions.js";
import firebase from "firebase/app";
import { formIsUsable } from "../../../components/DynamicForm/formToggle.js";
import { makeAttached } from "../../../utils.js";
import { reactive } from "vue";

/**
 * Add Design Change action factory.
 *
 * Returns a contextData-style action that opens the create form. All Firebase
 * paths and number formatting are derived from `variant`, so the same code
 * services both the STC and Military Design Change registers.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeAddDesignChange = (variant) => ({ form, pageItem, store }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcParentType = parentTypeFor(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");
    const dcSuperRefs = superRefs(variant, "DesignChanges");
    const isMilitary = variant.key === "military";

    form.setItem(pageItem);
    form.setConfig({
        "title": isMilitary ? "Add Military Design Change" : "Add Design Change",
        "description": `Add a${isMilitary ? " Military" : ""} Design Change to Commercial ${pageItem.Number}`,
        "type": "push",
        "location": dcCollection,
        "fields": [{
            "label": "Commercial",
            "type": "value",
            value() {
                return pageItem.Number;
            }
        }, {
            "label": "Responsible Engineer",
            "name": "ResponsibleEngineer",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dataLocation": "users",
            dataFilter(user) {
                return user.main;
            }
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dropdownData": [{
                "label": "Minor",
                "id": "Minor"
            }, {
                "label": "Major",
                "id": "Major"
            }]
        }, {
            "label": "ATA Chapter",
            "name": "ATAChapter",
            "type": "dropdown",
            "labelKey": "NameFull",
            "sortKey": "meta.Number",
            "idKey": "Number",
            "dataLocation": "Database/ATAChapters"
        }, {
            "label": "Aircraft Code",
            "name": "AircraftCode",
            "sortKey": "meta.Number",
            "type": "dropdown",
            "labelKey": "NameFull",
            "idKey": "Number",
            "dataLocation": "Database/AircraftCodes"
        }, {
            "label": "New Design Change Number",
            "type": "value",
            value({ dropdownData, liveInputData }) {
                const ata = dropdownData.ATAChapter.find(i => i.meta.Number == liveInputData.ATAChapter);
                const count = ata ? readCount(ata.meta, variant, "CountDesignChanges") : "";
                return `${variant.numberPrefix}${liveInputData.ATAChapter || ""}-${liveInputData.AircraftCode || ""}-${count}`;
            }
        }, {
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "col-span-3"
        }, {
            "label": "Actual Aircraft",
            "name": "ActualAircraft",
            "type": "aircraftSearch"
        }],
        preSubmitHook({ dropdownData, liveInputData }) {
            const ata = dropdownData.ATAChapter.find(i => i.meta.Number == liveInputData.ATAChapter);
            const ac = dropdownData.AircraftCode.find(i => i.meta.Number == liveInputData.AircraftCode);
            const serial = readCount(ata.meta, variant, "CountDesignChanges");
            return {
                "ATAChapterID": ata.meta.ID,
                "AircraftCodeID": ac.meta.ID,
                "Aircraft": ac.meta.Name,
                "Parent": pageItem.Number,
                "ParentID": pageItem.ID,
                "Number": `${variant.numberPrefix}${liveInputData.ATAChapter || ""}-${liveInputData.AircraftCode || ""}-${serial}`,
                "Commercials": {
                    [pageItem.ID]: true
                },
                "Customer": pageItem.Customer,
                "Cancelled": false,
                "Date": {
                    "Cancelled": "N/A",
                    "Completed": "N/A",
                    "Transferred": "N/A",
                    "Created": (new Date()).toISOString()
                },
                "Transferred": false,
                "Completed": false,
                "SerialNumber": serial,
                "LockedForRelease": false,
                "EASANumber": liveInputData.Type == "Minor" ? variant.approvalNumber : "Input on Issue"
            };
        },
        async postSubmitHook({ db, typeRef, data }) {
            data.ID = typeRef.key;

            // Increment the ATA chapter counter for this variant. Writes both
            // the new Counts map and the legacy field for migration safety.
            const ataUpdates = countUpdates(
                `Database/ATAChapters/${data.ATAChapterID}`,
                variant,
                "CountDesignChanges",
                parseInt(data.SerialNumber) + 1
            );
            await Promise.all(Object.entries(ataUpdates).map(([loc, val]) => db.set(loc, val)));

            // Backref on the Commercial — variant-scoped so the parent
            // contains a separate map of MilitaryDesignChanges.
            db.set(`Database/Commercials/${pageItem.ID}/${dcParentType}/${data.ID}`, true);

            // Variant-scoped Refs / SuperRefs so each project has its own
            // search index. (Previously only STC had refs written.)
            db.utils.updateRefs(data, dcRefs, ["Number", "ID", "Title", "Date", "Customer", "Cancelled", "Completed", "ResponsibleEngineer", "Parent", "EASANumber", "Type"]);
            db.utils.updateRefs(data, dcSuperRefs, ["ID", "Title", "ATAChapter", "EASANumber", "Type"], "DesignChanges");

            // Auto-create the standard documents for this Design Change. The
            // variant flows through so Documents land in the correct
            // collection with the correct number prefix.
            const docCollection = collection(variant, "Documents");
            const docCollectionTail = docCollection.split("/")[1];
            const docRefs = refs(variant, "Documents");
            const docSuperRefs = superRefs(variant, "Documents");
            const newDocs = await createBasicDocuments({ store, db, parent: data, parentType: dcParentType, variant });
            for (const doc of newDocs) {
                const ref = await db.push(docCollection, doc);
                doc.ID = ref.key;
                db.utils.updateRefs(doc, docRefs, ["Number", "ID", "Title", "Date", "Revision", "Parent", "ParentID", "SubDocumentType", "MPDS"]);
                db.utils.updateRefs({
                    "key": ref.key,
                    "c": doc.Number
                }, docSuperRefs, ["k", "c"]);
                await db.set(`${dcCollection}/${typeRef.key}/${docCollectionTail}/${ref.key}`, true);
                // bump the document type counter for this variant
                const docTypeUpdates = countUpdates(
                    `Database/DocumentTypes/${doc.TypeID}`,
                    variant,
                    "Count",
                    (doc.Count || 0) + 1
                );
                await Promise.all(Object.entries(docTypeUpdates).map(([loc, val]) => db.set(loc, val)));
            }

            // Yellow card. JobCards are shared across both projects; only the
            // ParentLocation differs so signoff hooks navigate to the right
            // Design Change record.
            const yellowCard = await createBasicYellowCard({ store, db, parent: data, variant });
            await db.set(`${dcCollection}/${typeRef.key}/JobCards/${typeRef.key}`, true);
            await db.update(`Database/JobCards/${typeRef.key}/`, yellowCard);
        }
    });
    form.getDropdownData();
    form.toggle();
};

/**
 * Add Transferred Design Change — used to capture a DC that originated
 * outside the system. The number is entered by the user instead of generated.
 */
export const makeAddTransferredDesignChange = (variant) => ({ form, pageItem }) => {
    const dcCollection = collection(variant, "DesignChanges");

    form.setItem(pageItem);
    form.setConfig({
        "title": "Add Design Change",
        "description": `Add a Design Change to Commercial ${pageItem.Number}`,
        "type": "push",
        "location": dcCollection,
        "fields": [{
            "label": "Commercial",
            "type": "value",
            value() {
                return pageItem.Number;
            }
        }, {
            "label": "Responsible Engineer",
            "name": "ResponsibleEngineer",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dataLocation": "users",
            dataFilter(user) {
                return user.main;
            }
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dropdownData": [{
                "label": "Minor",
                "id": "Minor"
            }, {
                "label": "Major",
                "id": "Major"
            }]
        }, {
            "label": "ATA Chapter",
            "name": "ATAChapter",
            "type": "dropdown",
            "labelKey": "NameFull",
            "sortKey": "meta.Number",
            "idKey": "Number",
            "dataLocation": "Database/ATAChapters"
        }, {
            "label": "Aircraft Code",
            "name": "AircraftCode",
            "sortKey": "meta.Number",
            "type": "dropdown",
            "labelKey": "NameFull",
            "idKey": "Number",
            "dataLocation": "Database/AircraftCodes"
        }, {
            "label": "New Design Change Number",
            "type": "text",
            "name": "Number"
        }, {
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "col-span-3"
        }],
        preSubmitHook({ dropdownData, liveInputData }) {
            const ata = dropdownData.ATAChapter.find(i => i.meta.Number == liveInputData.ATAChapter);
            const ac = dropdownData.AircraftCode.find(i => i.meta.Number == liveInputData.AircraftCode);
            return {
                "ATAChapterID": ata.meta.ID,
                "AircraftCodeID": ac.meta.ID,
                "Parent": pageItem.Number,
                "ParentID": pageItem.ID,
                "Commercials": {
                    [pageItem.ID]: true
                },
                "Customer": pageItem.Customer,
                "Cancelled": false,
                "Documents": true,
                "Date": {
                    "Cancelled": "N/A",
                    "Completed": "N/A",
                    "Transferred": "N/A",
                    "Created": (new Date()).toISOString()
                },
                "Transferred": true,
                "Completed": false,
                "SerialNumber": readCount(ata.meta, variant, "CountDesignChanges"),
                "EASANumber": liveInputData.Type == "Minor" ? variant.approvalNumber : "Input on Issue"
            };
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makeEditDesignChange = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setItem(item);
    form.setConfig({
        "title": "Edit Design Change",
        "description": `Edit Design Change ${item.Number}`,
        "type": "update",
        "location": `${dcCollection}/${item.ID}`,
        "fields": [{
            "label": "Responsible Engineer",
            "name": "ResponsibleEngineer",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dataLocation": "users",
            dataFilter(user) {
                return user.main;
            }
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "name",
            "idKey": "name",
            "dropdownData": [{
                "label": "Minor",
                "id": "Minor"
            }, {
                "label": "Major",
                "id": "Major"
            }]
        }, {
            "label": "Aircraft Code",
            "name": "AircraftCode",
            "sortKey": "meta.Number",
            "type": "dropdown",
            "labelKey": "NameFull",
            "idKey": "Number",
            "dataLocation": "Database/AircraftCodes"
        }, {
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "col-span-3"
        }, {
            "label": "Actual Aircraft",
            "name": "ActualAircraft",
            "type": "aircraftSearch"
        }, {
            "label": "Set Document Deadline Date",
            "name": "DocumentDeadlineDate",
            "type": "date",
            show() { 
                return appConfig.appPrefix !== "stc"
            }
        }],
        preSubmitHook({ liveInputData }) {
            // Reconstruct the Number from the existing parts so any prefix
            // segment (e.g. "M-") is preserved verbatim from the original.
            const numSplit = item.Number.split("-");
            const aircraftIdx = numSplit.length - 2;
            numSplit[aircraftIdx] = liveInputData.AircraftCode;
            return {
                "EASANumber": liveInputData.Type == "Minor" ? variant.approvalNumber : "Input on Issue",
                "Number": numSplit.join("-")
            };
        },
        async postSubmitHook({ db, newData }) {
            const refData = {
                ...item,
                ...newData,
                "ID": item.ID
            };
            await db.utils.updateRefs(refData, dcRefs, ["Number", "ID", "Title", "Date", "Customer", "Cancelled", "Completed", "ResponsibleEngineer", "Parent", "EASANumber", "Type"]);
            await checkDesignChangeJobCards(db, item.ID, variant);
        }
    });
    if (!formIsUsable(form)) return;
    form.autoSetValues();
    form.getDropdownData();
    form.toggle();
};

export const makeShareDesignChangeWithCustomer = (variant) => ({ form, pageItem, store }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setValues({
        CustomerID: pageItem.CustomerAccess?.CustomerID || ""
    });
    form.setConfig({
        "title": `Share ${pageItem.Number} with Customer`,
        "description": "Customer members will receive read-only access according to their category permissions.",
        "type": "update",
        "location": `${dcCollection}/${pageItem.ID}`,
        "fields": [{
            "label": "Customer",
            "name": "CustomerID",
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "ID",
            "dataLocation": "Database/Customers"
        }],
        preSubmitHook({ data }) {
            return {
                "CustomerID": null,
                "CustomerAccess": {
                    "Enabled": true,
                    "CustomerID": data.CustomerID,
                    "GrantedBy": store.getters.userName,
                    "GrantedAt": new Date().toISOString(),
                    "RevokedAt": null
                }
            };
        },
        async postSubmitHook({ db, data }) {
            await db.update(`${dcRefs}/${pageItem.ID}/CustomerAccess`, {
                "Enabled": true,
                "CustomerID": data.CustomerAccess.CustomerID
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makeRevokeDesignChangeCustomerAccess = (variant) => ({ form, pageItem, store }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setConfig({
        "title": `Revoke Customer Access to ${pageItem.Number}`,
        "description": "Customer members will immediately lose access to this Design Change and its projected items.",
        "submitButtonLabel": "Revoke Access",
        "submitButtonClass": "cancel",
        "type": "update",
        "location": `${dcCollection}/${pageItem.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "CustomerAccess": {
                    "Enabled": false,
                    "RevokedBy": store.getters.userName,
                    "RevokedAt": new Date().toISOString()
                }
            };
        },
        async postSubmitHook({ db }) {
            await db.set(`${dcRefs}/${pageItem.ID}/CustomerAccess/Enabled`, false);
        }
    });
    form.toggle();
};

export const makeCancelDesignChange = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setItem(item);
    form.setConfig({
        "title": "Cancel Design Change",
        "description": `Are you sure you want to cancel Design Change ${item.Number}?`,
        "submitButtonLabel": "Cancel",
        "submitButtonClass": "cancel",
        "type": "update",
        "location": `${dcCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Cancelled": true,
                "Date/Cancelled": new Date().toISOString()
            };
        },
        async postSubmitHook({ db }) {
            await db.update(`Database/JobCards/${item.ID}`, {
                "Cancelled": true,
                "Date/Cancelled": new Date().toISOString()
            });
            await db.set(`${dcRefs}/${item.ID}/Cancelled`, true);
            await checkDesignChangeJobCards(db, item.ID, variant);
        }
    });
    form.toggle();
};

export const makeCompleteDesignChange = (variant) => ({ form, item, store }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setItem(item);
    form.setConfig({
        "title": "Complete Design Change",
        "description": `Are you sure you want to complete this Design Change ${item.Number}?`,
        "submitButtonLabel": "Complete",
        "type": "update",
        "location": `${dcCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Completed": true,
                "Date/Completed": new Date().toISOString()
            };
        },
        async postSubmitHook({ db }) {
            const aircraft = await db.once("Database/AircraftCodes/");
            const ataChapters = await db.once("Database/ATAChapters/");
            await db.set(`${dcRefs}/${item.ID}/Completed`, true);
            await db.set(`OpenForms/OpenData/DesignChanges/${item.ID}`, {
                title: item.Title,
                aircraft: aircraft[item.AircraftCodeID].Name,
                ataChapter: ataChapters[item.ATAChapterID].Name,
                dateCompleted: new Date().toISOString()
            });
            const jobCard = await db.once(`Database/JobCards/${item.ID}`);
            jobCardSignoffCheckList("Engineer", "EngineerResponsibilities")({ form, item: jobCard, db, store });
        }
    });
    form.toggle();
};

export const makeLinkDesignChange = (variant) => ({ form, pageItem, db, meta }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");
    const dcParentType = parentTypeFor(variant, "DesignChanges");

    form.setConfig({
        "title": "Link Design Change",
        "description": `Link a Design Change to ${pageItem.Number}`,
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${dcParentType}`,
        "fields": [{
            "label": "Design Change",
            "name": "_doc",
            "type": "dropdown",
            "dataLocation": dcRefs,
            "labelKey": "Number",
            "idKey": "ID",
            "search": false
        }],
        async preSubmitHook({ data }) {
            return {
                "_doc": null,
                [data._doc]: true
            };
        },
        async postSubmitHook({ liveInputData }) {
            await db.set(`${dcCollection}/${liveInputData._doc}/${meta.ParentType}/${pageItem.ID}`, true);
            await checkDesignChangeJobCards(db, pageItem.ID, variant);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makeUnlinkDesignChange = (variant) => ({ form, pageItem, rowItem, meta, db }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcParentType = parentTypeFor(variant, "DesignChanges");

    form.setConfig({
        "title": "Unlink Design Change",
        "type": "delete",
        "location": `${dcCollection}/${rowItem.ID}/${meta.ParentType}/${pageItem.ID}`,
        "fields": [],
        async postSubmitHook() {
            db.delete(`Database/${meta.ParentType}/${pageItem.ID}/${dcParentType}/${rowItem.ID}`);
            db.delete(`Database/${meta.ParentType}/${pageItem.ID}/Links/${rowItem.ID}`);
            await checkDesignChangeJobCards(db, pageItem.ID, variant);
        }
    });
    form.toggle();
};

/**
 * Import Job Cards from a linked Design Change into the parent Commercial.
 * JobCards remain shared across both project variants, so this action is
 * variant-agnostic in behaviour — exposed as a factory only for API symmetry.
 */
export const makeImportDesignChangesJobCards = (_variant) => ({ form, pageItem, rowItem, meta }) => {
    form.setValues({
        _cards: Object.keys(rowItem.JobCards)
    });

    form.setConfig({
        "title": "Import Job Cards",
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/JobCards`,
        "fields": [{
            "label": "Job Cards",
            "name": "_cards",
            "type": "dropdown",
            "dataLocation": "Database/JobCards/",
            "dataKeys": Object.keys(rowItem.JobCards),
            "idKey": "ID",
            "multiple": true,
            "labelKey": "Number"
        }],
        preSubmitHook({ liveInputData }) {
            return {
                ...makeAttached(liveInputData._cards),
                _cards: null
            };
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makeHideDesignChange = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setItem(item);
    form.setConfig({
        "title": "Hide Design Change from the main list of Design Changes",
        "submitButtonLabel": "Hide",
        "type": "update",
        "location": `${dcCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Hidden": true,
                "Date/Hidden": new Date().toISOString()
            };
        },
        async postSubmitHook({ db }) {
            await db.set(`${dcRefs}/${item.ID}/Hidden`, true);
            await checkDesignChangeJobCards(db, item.ID, variant);
        },
    });
    form.toggle();
};

export const makeUnhideDesignChange = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    form.setItem(item);
    form.setConfig({
        "title": "Unhide Design Change from the main list of Design Changes",
        "submitButtonLabel": "Unhide",
        "type": "update",
        "location": `${dcCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Hidden": false,
                "Date/Hidden": new Date().toISOString()
            };
        },
        async postSubmitHook({ db }) {
            await db.set(`${dcRefs}/${item.ID}/Hidden`, false);
            await checkDesignChangeJobCards(db, item.ID, variant);
        },
    });
    form.toggle();
};

export const makeLockForRelease = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");

    form.setConfig({
        "title": "Lock For Release",
        "description": `Are you sure you want to lock this Design Change ${item.Number} for release?`,
        "location": `${dcCollection}/${item.ID}`,
        "type": "update",
        "fields": [],
        preSubmitHook() {
            return {
                "LockedForRelease": true
            };
        }
    });
    form.toggle();
};

export const makeUnlockForRelease = (variant) => ({ form, item }) => {
    const dcCollection = collection(variant, "DesignChanges");

    form.setConfig({
        "title": "Unlock For Release",
        "description": `Are you sure you want to unlock this Design Change ${item.Number} for release?`,
        "location": `${dcCollection}/${item.ID}`,
        "type": "update",
        "fields": [],
        preSubmitHook() {
            return {
                "LockedForRelease": false
            };
        }
    });
    form.toggle();
};

/**
 * Walks all JobCards attached to a Design Change and updates its completion
 * status based on the Yellow Card signoff state. Variant-aware so it operates
 * on the correct collection.
 */
export async function checkDesignChangeJobCards(db, id, variant) {
    const dcCollection = collection(variant, "DesignChanges");
    const dcRefs = refs(variant, "DesignChanges");

    const designChange = await db.once(`${dcCollection}/${id}`);
    const jobCardKeys = Object.keys(designChange?.JobCards || {});
    const jobCards = await Promise.all(jobCardKeys.map(key => db.once(`Database/JobCards/${key}`)));
    jobCards.filter(i => !!i).filter(card => card?.Cancelled !== true);

    const allCompleted = jobCards.every(card => card?.Signedoff?.Engineer === true);

    const yellowCard = jobCards.find(card => card.Type === "Yellow Card");

    if (yellowCard && yellowCard.Signedoff?.Engineer === true) {
        const yellowCardSignoffDate = new Date(yellowCard.Date?.SignedOff || Date.now()).toISOString();
        await db.set(`${dcCollection}/${id}/Date/Completed`, yellowCardSignoffDate);
        await db.set(`${dcRefs}/${id}/Completed`, allCompleted);
    }
}

export const summaryLoadingFlags = reactive({});

export const makeSummariseDesignChange = (variant) => async ({ pageItem, toast }) => {
    const dcCollection = collection(variant, "DesignChanges");
    try {
        if (!pageItem?.ID) {
            toast.error("Unable to summarise design change: ID is missing.");
            return;
        }

        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
            toast.error("Unable to summarise design change: database configuration is missing.");
            return;
        }

        summaryLoadingFlags[pageItem.ID] = true;
        toast.info("Generating design change summary. This may take a moment...");
        const summarise = firebase.app().functions().httpsCallable("summariseDesignChange", { timeout: 180000 });
        await summarise({
            id: pageItem.ID,
            databaseURL,
            collection: dcCollection,
        });
        toast.success("Design change summary generated successfully.");
    } catch (error) {
        console.error("[DesignChanges][Summary] Callable failed", error);
        toast.error(error?.message || "Failed to generate design change summary.");
    } finally {
        summaryLoadingFlags[pageItem.ID] = false;
    }
};
